// components/MyPage/UserSettings/UserInfoForm.jsx
import { useState, useEffect } from 'react';
import {
    Card, CardContent, TextField, MenuItem,
    InputAdornment, Stack, Typography,
    useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { LoadingButton } from '@mui/lab';
import api from '../../../api/axios';
import { useUser } from '../../../contexts/UserContext';

const GENDER_OPTIONS = ['멜', '펨'];
const preferenceOptions = {
    멜: ['MD', 'MS', 'MSW', 'MSD', 'MSS'],
    펨: ['FD', 'FS', 'FSW', 'FSD', 'FSS'],
};

const yearOptions = Array.from({ length: 21 }, (_, i) => 2010 - i); // 2010~1990

export default function UserInfoForm({ setSnack }) {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const [username, setUsername] = useState('');
    const [gender, setGender] = useState('');
    const [preference, setPreference] = useState('');
    const [region, setRegion] = useState('');
    const [birthYear, setBirthYear] = useState('');
    const [checking, setChecking] = useState(false);
    const [saving, setSaving] = useState(false);
    const [uError, setUError] = useState('');

    const { refreshUserInfo } = useUser();

    useEffect(() => {
        api.get('/api/user-info/')
            .then(({ data }) => {
                setUsername(data.username || '');
                setGender(data.gender || '');
                setPreference(data.preference ?? data.tendency ?? '');
                setRegion(data.region_code || '');
                setBirthYear(data.birth_year || '');
            })
            .catch(() =>
                setSnack({ open: true, msg: '유저 정보를 불러오지 못했습니다.', severity: 'error' })
            );
    }, [setSnack]);

    const checkUsername = () => {
        if (!username) return;
        setChecking(true);
        api.get(`/api/accounts/user/check-username/?username=${username}`)
            .then(({ data }) =>
                setUError(data.exists ? '이미 사용 중인 닉네임입니다.' : '사용 가능한 닉네임입니다.')
            )
            .catch(() => setUError('중복 체크 실패'))
            .finally(() => setChecking(false));
    };

    const handleSave = () => {
        if (saving || uError === '이미 사용 중인 닉네임입니다.') return;
        setSaving(true);
        api.post('/api/accounts/user/update/', {
            username,
            gender,
            preference,
            region_code: region,
            birth_year: birthYear,
        })
            .then(() => {
                refreshUserInfo();
                setSnack({ open: true, msg: '저장 완료!', severity: 'success' });
            })
            .catch(() =>
                setSnack({ open: true, msg: '저장 실패', severity: 'error' })
            )
            .finally(() => setSaving(false));
    };

    return (
        <Card sx={{ mb: 4 }}>
            <CardContent>
                <Typography
                    variant={isMobile ? 'h6' : 'h5'}
                    fontWeight="bold"
                    sx={{ mb: 3 }}
                >
                    🛠 회원정보 수정
                </Typography>

                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                    <TextField
                        fullWidth required label="닉네임"
                        value={username}
                        onChange={(e) => { setUsername(e.target.value); setUError(''); }}
                        error={!!uError && uError !== '사용 가능한 닉네임입니다.'}
                        helperText={uError || ' '}
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <LoadingButton
                                        size="small"
                                        loading={checking}
                                        onClick={checkUsername}
                                        variant="outlined"
                                        sx={{
                                            minWidth: 'auto',
                                            height: isMobile ? 32 : 36,
                                            fontSize: isMobile ? '0.75rem' : '0.875rem',
                                            px: 1.5,
                                        }}
                                    >
                                        중복확인
                                    </LoadingButton>
                                </InputAdornment>
                            ),
                        }}
                    />

                    <TextField
                        select required fullWidth label="성별"
                        value={gender}
                        onChange={(e) => { setGender(e.target.value); setPreference(''); }}
                    >
                        {GENDER_OPTIONS.map((opt) => (
                            <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                        ))}
                    </TextField>

                    <TextField
                        select fullWidth label="성향"
                        value={preference}
                        onChange={(e) => setPreference(e.target.value)}
                        disabled={!gender}
                    >
                        {(preferenceOptions[gender] || []).map((opt) => (
                            <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                        ))}
                    </TextField>

                    <TextField
                        fullWidth label="지역번호"
                        value={region}
                        onChange={(e) => setRegion(e.target.value.replace(/[^0-9]/g, ''))}
                    />
                </Stack>

                <TextField
                    select fullWidth label="출생년도" sx={{ mt: 2 }}
                    value={birthYear}
                    onChange={(e) => setBirthYear(e.target.value)}
                >
                    <MenuItem value="">출생년도 선택</MenuItem>
                    {yearOptions.map((year) => (
                        <MenuItem key={year} value={year}>
                            {year}년생
                        </MenuItem>
                    ))}
                </TextField>

                <LoadingButton
                    variant="contained"
                    loading={saving}
                    fullWidth
                    sx={{ mt: 3, py: 1.2 }}
                    onClick={handleSave}
                >
                    저장
                </LoadingButton>
            </CardContent>
        </Card>
    );
}
